import React, { useEffect, useState } from "react";
import Button from "../components/Buttons/Button";
import imageUrl from "../assets/imgs/cardImage2.jpg";
import GalCard from "../components/Gallery-cards/GalCard";
import { Plus, ArrowLeft, ArrowRight } from "lucide-react";
import AddServiceImageModal from "../components/models/AddServiceImaeModal";
import EditServiceModal from "../components/models/EditServiceModal";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import Loader from "../components/Loader.jsx";
import ServiceShimmerCard from "../components/Shimmers/ServiceShimmerCard.jsx";
import {
  fetchProviderList,
  fetchCountry,
  DeleteProviderDetails,
} from "../redux/slices/ProvidergalSlice";

const Servicegallery = () => {
  const dispatch = useDispatch();

  // ---------------- STATES ----------------
  const [page, setPage] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
 

  const { providerList, loading, totalPages, countryList } = useSelector(
    (state) => state.providerGallery
  );

  const isOverlayLoading = loading && !initialLoad;

  // ---------------- FETCH INITIAL DATA ----------------
  useEffect(() => {
    const loadInitial = async () => {
      await dispatch(fetchCountry());
      await dispatch(fetchProviderList({ page: 1, country_id: "" }));
      setInitialLoad(false);
    };
    loadInitial();
  }, [dispatch]);

  // ---------------- FETCH ON PAGE OR FILTER CHANGE ----------------
  useEffect(() => {
    if (!initialLoad) {
      dispatch(fetchProviderList({ page, country_id: selectedCountry }));
    }
  }, [page, selectedCountry, dispatch, initialLoad]);

  // ---------------- DELETE HANDLER ----------------
  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will delete the image permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(DeleteProviderDetails({ provider_image_gallery_id: id }))
          .unwrap()
          .then(() => {
            Swal.fire("Deleted!", "Image has been deleted.", "success");
            if (providerList.length === 1 && page > 1) {
              setPage(page - 1);
            } else {
              dispatch(
                fetchProviderList({ page, country_id: selectedCountry })
              );
            }
          })
          .catch(() => {
            Swal.fire("Error!", "Unable to delete image.", "error");
          });
      }
    });
  };

  // ---------------- EDIT HANDLER ----------------
  const handleEdit = (provider) => {
    setEditData(provider);
    setEditModalOpen(true);
  };

  // ---------------- MODAL CLOSE ----------------
  const handleAddModalClose = () => {
    setAddModalOpen(false);
    dispatch(fetchProviderList({ page, country_id: selectedCountry }));
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setEditData(null);
    dispatch(fetchProviderList({ page, country_id: selectedCountry }));
  };

  // ---------------- PAGINATION ----------------
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const handlePrevious = () => {
      if (!isOverlayLoading && page > 1) setPage((prev) => prev - 1);
    };
    const handleNext = () => {
      if (!isOverlayLoading && page < totalPages) setPage((prev) => prev + 1);
    };
    const handlePageClick = (p) => {
      if (!isOverlayLoading) setPage(p);
    };

    return (
      <div className="flex justify-center py-6">
        <div className="flex gap-2">
          <button
            onClick={handlePrevious}
            disabled={page === 1 || isOverlayLoading}
            className="px-3 py-1 border rounded disabled:bg-gray-200 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> Previous
          </button>

          <div className="flex space-x-1">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => handlePageClick(i + 1)}
                className={`px-3 py-1 rounded text-sm ${
                  page === i + 1
                    ? "bg-orange-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            onClick={handleNext}
            disabled={page === totalPages || isOverlayLoading}
            className="px-3 py-1 border rounded disabled:bg-gray-200 disabled:cursor-not-allowed flex items-center gap-1"
          >
            Next <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // ---------------- RENDER ----------------
  if (initialLoad) return <Loader />;

  return (
    <>
      <div>
        {/* Page Title */}
        <div className="flex flex-col md:flex-row justify-between border-b border-gray-200 py-4">
          <div className="py-2">
            <h1 className="text-xl md:text-2xl text-gray-800 font-bold">
              Service Gallery Management
            </h1>
            <p className="text-md text-gray-400 mt-2 font-normal">
              Manage Images for the service gallery by country
            </p>
          </div>
          <div>
            <Button
              classes="w-full sm:w-[200px] hover:text-white"
              text={"Add New Image"}
              icon={<Plus size={20} />}
              iconPosition="left"
              widthSize={200}
              onClick={() => setAddModalOpen(true)}
            />
          </div>
        </div>

        {/* Filter */}
        <div className="py-4">
          <div className="flex flex-col md:flex-row justify-start gap-4">
            <div className="flex flex-col gap-1 mt-2">
              <label
                htmlFor="selectCountry"
                className="text-sm font-medium text-gray-700"
              >
                Country
              </label>

              <select
                id="selectCountry"
                value={selectedCountry}
                onChange={(e) => {
                  setSelectedCountry(e.target.value);
                  setPage(1);
                }}
                className="p-2.5 border border-gray-300 rounded-md bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#FF6943]/40 w-64 cursor-pointer"
              >
                <option value="">All</option>
                {countryList?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.country_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 py-4">
            {isOverlayLoading ? (
              <>
                {Array.from({ length: 8 }).map((_, i) => (
                  <ServiceShimmerCard key={i} />
                ))}
              </>
            ) : providerList?.length > 0 ? (
              providerList.map((provider, index) => (
                <GalCard
                  key={`${provider.id}-${index}`}
                  providerId={provider.id}
                  cardImage={provider.image || imageUrl}
                  cardTitle={provider.name}
                  countryName={provider.country}
                  showMainToggle={true}
                  showButtons={true}
                  showSmallToggle={false}
                  status={provider.status}
                  onDelete={() => handleDelete(provider.id)}
                  onEdit={() => handleEdit(provider)}
                />
              ))
            ) : (
              <p className="text-center text-gray-500 mt-4">
                No Providers found
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Pagination */}
      {renderPagination()}

      {/* Modals */}
      <AddServiceImageModal
        isOpen={addModalOpen}
        onClose={handleAddModalClose}
      />
      <EditServiceModal
        isOpen={editModalOpen}
        onClose={handleEditModalClose}
        providerId={editData ? editData.id : null}
      />
    </>
  );
};

export default Servicegallery;
